import React, { useEffect } from "react";
import axios from "axios";
import { API_BASE_URL, API_URLS, LOCALSTORAGE_TOKEN_ITEM } from "constants/api";
import { Analytics } from "types/analytics";
import moment from "moment/moment";

export interface IFilters {
  filterArtistIds?: string[];
  filterPlatformIds?: string[];
  filterLabelIds?: string[];
  filterTerritoryIds?: string[];
  filterReleaseIds?: string[];
  filterTrackIds?: string[];
  filterDate?: Date[];
}

let controller: null | AbortController = null;

export const useAnalytics = (
    filterArtistIds: IFilters['filterArtistIds'],
    filterPlatformIds: IFilters['filterPlatformIds'],
    filterLabelIds: IFilters['filterLabelIds'],
    filterTerritoryIds: IFilters['filterTerritoryIds'],
    filterReleaseIds: IFilters['filterReleaseIds'],
    filterTrackIds: IFilters['filterTrackIds'],
    filterDate: IFilters['filterDate'],
) => {
  const [analytics, setAnalytics] = React.useState<Analytics>();
  const [countries, setCountries] = React.useState<Analytics>();
  const [fetching, setFetching] = React.useState(true);
  const [total, setTotal] = React.useState(0);
  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM);

  useEffect(() => {
    // default this year if filterDate is empty
    const defaultFilterDate = filterDate?.length === 0
        ? [moment().startOf('year').toDate(), moment().toDate()]
        : filterDate;

    (async () => await getAnalytics(
        { filterReleaseIds, filterTrackIds, filterDate: defaultFilterDate, filterArtistIds, filterPlatformIds, filterLabelIds, filterTerritoryIds }
    ))();
  }, [filterReleaseIds, filterTrackIds, filterDate, filterArtistIds, filterPlatformIds, filterLabelIds, filterTerritoryIds]);

  const getAnalytics = async (
      filters: IFilters = {},
  ) => {
    try {
      setFetching(true);
      const [from, to] = filters.filterDate || [];
      const queryParams = new URLSearchParams({
        metric: 'streams',
        from: from ? moment(from).format('YYYY-MM-DD') : '',
        to: to ? moment(to).format('YYYY-MM-DD') : '',
        artist_ids: prepareFilter('artist_ids', filters.filterArtistIds),
        country_ids: prepareFilter('country_ids', filters.filterTerritoryIds),
        label_ids: prepareFilter('label_ids', filters.filterLabelIds),
        platform_ids: prepareFilter('platform_ids', filters.filterPlatformIds),
        track_ids: prepareFilter('track_ids', filters.filterTrackIds),
        release_ids: prepareFilter('release_ids', filters.filterReleaseIds),
      } as any);
      const query = Array.from(queryParams.entries())
          .map(param => {
            if (['artist_ids', 'country_ids', 'label_ids', 'platform_ids', 'track_ids', 'release_ids'].includes(param[0])) {
              return param[1];
            }

            if (param[1]) {
              return `${param[0]}=${param[1]}`;
            }
            return '';
          })
          .filter(param => !!param)
          .join('&');

      if (controller) {
        controller.abort();
      }

      controller = new AbortController();
      const signal = controller.signal;

      const response = await axios.get(
          `${API_BASE_URL}${API_URLS.Analytics}?${query}`,
          { headers: { Accept: 'application/json', Authorization: `Bearer ${token}` }, signal }
      );
      setAnalytics(response.data.data);
      setTotal(response.data.data.totals.value);
      setFetching(false);
      setCountries(response.data.data.countries);
    } catch (e) {
      console.error(e);
    } finally {
      // setFetching(false);
    }
  };

  return { analytics, total, countries, fetching };
};

const prepareFilter = (key: string, ids?: string[]) => {
  return ids?.length ? ids.map(id => `${key}[]=${id}`).join("&") : '';
};